import React, { useState, useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import ItemService from "../../../../services/customer_appCommonService";
import { useSelector } from "react-redux";
import { InputField } from "../../../../Components/formElements/InputField";

// Adjust to match your accounts team roleId
const TALLY_ALLOWED_ROLES = ["FI-EXECUTIVE", "ACCOUNTS"];

const formatIST = (isoString) => {
  if (!isoString) return "--";
  return new Date(isoString).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const ERFTallyStatusSection = ({ documentNumber, isExtension = false }) => {
  const userData = useSelector((state) => state?.user);
  const currentUserId = userData?.data?.userId || "";

  // Only accounts team can add entries — everyone else sees read-only
  const canManageTally = useMemo(
    () =>
      userData?.data?.roles?.some((r) =>
        TALLY_ALLOWED_ROLES.includes(r.roleId),
      ) || false,
    [userData],
  );

  const [erfRecord, setErfRecord] = useState(null);
  const [tallyHistory, setTallyHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSectionVisible, setIsSectionVisible] = useState(true);

  const [form, setForm] = useState({
    tallyVoucherNo: "",
    tallyEntryDate: new Date().toISOString().split("T")[0],
    tallyStatus: "Done",
    tallyRemarks: "",
  });

  useEffect(() => {
    if (documentNumber) fetchERFData();
    // eslint-disable-next-line
  }, [documentNumber]);

  const fetchERFData = async () => {
    setLoading(true);
    try {
      const response = await ItemService.generateQuery({
        queryName: isExtension ? "ERFExtDetail" : "ERFDetail",
        documentNumber,
        timeZone: "Asia/Calcutta",
      });
      if (
        response?.status === 200 &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        const record = response.data[0];
        setErfRecord(record);
        setTallyHistory(record.tallyHistory || []);
      }
    } catch {
      toast.error("Failed to load tally status data");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.tallyVoucherNo.trim()) {
      toast.error("Tally Voucher Number is required");
      return;
    }
    if (!form.tallyEntryDate) {
      toast.error("Entry Date is required");
      return;
    }

    const newEntry = {
      tallyVoucherNo: form.tallyVoucherNo.trim(),
      tallyEntryDate: form.tallyEntryDate,
      tallyStatus: form.tallyStatus,
      tallyRemarks: form.tallyRemarks.trim(),
      enteredBy: currentUserId,
      enteredAt: new Date().toISOString(),
    };

    const updatedHistory = [...tallyHistory, newEntry];

    const payload = {
      documentNumber: erfRecord.documentNumber,
      equipmentRentNr: erfRecord.documentNumber,
      tallyHistory: updatedHistory,
      tallyStatusIndicator: form.tallyStatus,
      tallyVoucherNo: form.tallyVoucherNo.trim(),
    };

    try {
      setLoading(true);
      if (isExtension) {
        await ItemService.updateExtendEquipmentRent(payload);
      } else {
        await ItemService.updateEquipmentRentForm(payload);
      }
      toast.success(
        `Tally entry saved — ${
          form.tallyStatus === "Done" ? "Marked as Done" : "Marked as Not Done"
        }`,
      );
      setForm({
        tallyVoucherNo: "",
        tallyEntryDate: new Date().toISOString().split("T")[0],
        tallyStatus: "Done",
        tallyRemarks: "",
      });
      await fetchERFData();
    } catch {
      toast.error("Failed to save tally entry");
    } finally {
      setLoading(false);
    }
  };

  const latestStatus =
    tallyHistory.length > 0
      ? tallyHistory[tallyHistory.length - 1].tallyStatus
      : "Pending";

  const statusBadgeStyle = {
    Done: { bg: "#198754" },
    "Not Done": { bg: "#dc3545" },
    Pending: { bg: "#6c757d" },
  };
  const badge = statusBadgeStyle[latestStatus] || statusBadgeStyle.Pending;

  const columns = [
    {
      name: "Voucher No",
      cell: (row) => (
        <span className="fw-bold text-primary">{row.tallyVoucherNo}</span>
      ),
      width: "160px",
    },
    {
      name: "Entry Date",
      cell: (row) => <span>{row.tallyEntryDate || "--"}</span>,
      width: "130px",
    },
    {
      name: "Status",
      cell: (row) => {
        const s = statusBadgeStyle[row.tallyStatus] || statusBadgeStyle.Pending;
        return (
          <span
            className="badge"
            style={{
              backgroundColor: s.bg,
              color: "white",
              padding: "5px 10px",
              borderRadius: "8px",
              fontSize: "11px",
            }}
          >
            {row.tallyStatus}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: "Remarks",
      cell: (row) => <span className="text-muted">{row.tallyRemarks || "--"}</span>,
      width: "220px",
    },
    {
      name: "Entered By / At",
      cell: (row) => (
        <div>
          <p className="m-0 fw-medium" style={{ fontSize: "12px" }}>
            {row.enteredBy}
          </p>
          <p className="m-0 text-muted" style={{ fontSize: "10px" }}>
            {formatIST(row.enteredAt)}
          </p>
        </div>
      ),
      width: "180px",
    },
  ];

  if (!erfRecord) return null;

  return (
    <div className="accordion mt-4" id="accordionTallyStatus">
      <div className="accordion-item">
        <h2 className="accordion-header">
          <Row className="accordion-button">
            <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
              <i
                className={`bi ${
                  isSectionVisible ? "bi-chevron-right" : "bi-chevron-down"
                }`}
                onClick={() => setIsSectionVisible(!isSectionVisible)}
              />
            </Col>
            <Col xs={1} md={2} lg={2} className="text-center logoBtn">
              <span className="iconLogo">TS</span>
            </Col>
            <Col xs={12} md={6} lg={6}>
              <span className="headerTitle">Tally Status</span>
              <span className="subHeaderTitle">
                Accounts team tally voucher entry and reconciliation
              </span>
            </Col>
            <Col xs={12} md={3} lg={3} className="headerButtonAlign">
              <span
                className="badge"
                style={{
                  backgroundColor: badge.bg,
                  color: "white",
                  fontSize: "13px",
                  padding: "6px 12px",
                }}
              >
                {latestStatus}
              </span>
            </Col>
          </Row>
        </h2>

        <div
          className={`accordion-collapse collapse show ${
            isSectionVisible ? "visible" : ""
          }`}
        >
          <div className="accordion-body">
            {/* ── Entry Form — visible to all, editable only for accounts ── */}
            <div
              className="p-3 mb-3 rounded"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
              }}
            >
              <Row className="align-items-end">
                {/* Tally Voucher No */}
                <Col
                  xs={12}
                  md={6}
                  lg={3}
                  className="commTopButtonRightLeftPadding mb-2"
                >
                  <InputField
                    className="inputBox"
                    label="Tally Voucher No"
                    name="tallyVoucherNo"
                    placeholder="e.g. TV-2024-001"
                    value={form.tallyVoucherNo}
                    onChange={handleFormChange}
                    disabled={!canManageTally}
                    required={canManageTally}
                  />
                </Col>

                {/* Entry Date */}
                <Col
                  xs={12}
                  md={6}
                  lg={2}
                  className="commTopButtonRightLeftPadding mb-2"
                >
                  <InputField
                    className="inputBox"
                    label="Entry Date"
                    name="tallyEntryDate"
                    type="date"
                    value={form.tallyEntryDate}
                    onChange={handleFormChange}
                    disabled={!canManageTally}
                    required={canManageTally}
                    min="2000-01-01"
                  />
                </Col>

                {/* Status — native select, InputField doesn't support select type */}
                <Col
                  xs={12}
                  md={6}
                  lg={2}
                  className="commTopButtonRightLeftPadding mb-2"
                >
                  <div className="relative mb-1">
                    <label className="block text-sm font-medium leading-6 text-gray-900 infoIconAccordion">
                      Status <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      className="inputBox block w-full rounded-md py-2 text-gray-600 ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
                      name="tallyStatus"
                      value={form.tallyStatus}
                      onChange={handleFormChange}
                      disabled={!canManageTally}
                    >
                      <option value="Done">Done</option>
                      <option value="Not Done">Not Done</option>
                    </select>
                  </div>
                </Col>

                {/* Remarks */}
                <Col
                  xs={12}
                  md={6}
                  lg={3}
                  className="commTopButtonRightLeftPadding mb-2"
                >
                  <InputField
                    className="inputBox"
                    label="Remarks"
                    name="tallyRemarks"
                    placeholder="Remarks"
                    value={form.tallyRemarks}
                    onChange={handleFormChange}
                    disabled={!canManageTally}
                  />
                </Col>

                {/* Save — only accounts team sees this */}
                {canManageTally && (
                  <Col
                    xs={12}
                    md={6}
                    lg={2}
                    className="commTopButtonRightLeftPadding mb-2 d-flex align-items-end"
                  >
                    <button
                      className="btnTable w-100"
                      onClick={handleSave}
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Entry"}
                    </button>
                  </Col>
                )}
              </Row>
            </div>

            {/* ── History Table — visible to all ── */}
            <DataTable
              columns={columns}
              data={[...tallyHistory].reverse()}
              pagination
              noDataComponent={
                <div className="p-3 text-center text-muted">
                  No tally entries recorded yet
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERFTallyStatusSection;
